import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { ORDER_STATUS, PAYMENT_LABEL } from "@/lib/constants";
import { StatusBadge } from "@/pages/OrderPage";
import { useSelection, CheckAll, Check, BulkBar, runBulk, bulkInp } from "@/components/admin/Bulk";

const TrackingForm = ({ o, onSaved }) => {
  const [f, setF] = useState({ carrier: o.carrier || "BRT", tracking_number: o.tracking_number || "", tracking_url: o.tracking_url || "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/orders/${o.id}/tracking`, f);
      toast.success("Tracking salvato");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const inp = "h-9 rounded-lg border border-black/15 bg-white px-2 text-xs";
  return (
    <form onSubmit={save} className="mt-4 flex flex-wrap items-end gap-2" data-testid={`tracking-form-${o.id}`}>
      <select value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value })} className={inp} data-testid="tracking-carrier">
        {["BRT", "GLS", "SDA", "Poste Italiane", "DHL", "UPS", "InPost"].map((c) => <option key={c}>{c}</option>)}
      </select>
      <input required value={f.tracking_number} onChange={(e) => setF({ ...f, tracking_number: e.target.value })} placeholder="Numero di tracking" className={inp} data-testid="tracking-number" />
      <input value={f.tracking_url} onChange={(e) => setF({ ...f, tracking_url: e.target.value })} placeholder="Link tracking (facoltativo)" className={`${inp} min-w-[220px] flex-1`} data-testid="tracking-url" />
      <button className="h-9 rounded-lg bg-black px-4 text-xs text-white" data-testid="tracking-save">Salva tracking</button>
    </form>
  );
};

export const AdminOrders = ({ onChange, presets }) => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(presets ? presets[0][0] : "");
  const [open, setOpen] = useState(null);
  const [bulkStatus, setBulkStatus] = useState("");
  const s = useSelection(orders);
  const load = () => api.get("/admin/orders", { params: { status: filter || undefined } }).then((r) => setOrders(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const after = (r) => { if (r) { s.clear(); setBulkStatus(""); load(); onChange?.(); } };
  const applyStatus = async () => {
    if (!bulkStatus) return toast.error("Scegli il nuovo stato");
    if (bulkStatus === "annullato" && !window.confirm(`Annullare ${s.ids.length} ordini? Lo stock verrà ripristinato.`)) return;
    after(await runBulk("/admin/bulk/orders", { ids: s.ids, action: "status", status: bulkStatus }, `Stato "${ORDER_STATUS[bulkStatus].label}"`));
  };
  const bulkDelete = async () => {
    const paid = orders.filter((o) => s.ids.includes(o.id) && ["paid", "partial"].includes(o.payment_status)).length;
    if (!window.confirm(`Eliminare ${s.ids.length} ordini?${paid ? ` ${paid} ${paid === 1 ? "è già pagato" : "sono già pagati"}: verranno eliminati senza rimborso, senza toccare lo stock, insieme alle loro fatture.` : ""} L'operazione non si può annullare.`)) return;
    after(await runBulk("/admin/bulk/orders", { ids: s.ids, action: "delete" }, "Ordini eliminati"));
  };

  const update = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Stato aggiornato: ${ORDER_STATUS[status].label}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {(presets || [["", "Tutti"], ...Object.entries(ORDER_STATUS).map(([k, v]) => [k, v.label])]).map(([k, l]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`admin-order-filter-${k || "all"}`}
            className={`rounded-full border px-3 py-1.5 text-xs ${filter === k ? "border-amber-500 bg-amber-500/15 text-amber-700" : "border-black/10 text-stone-600"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-4">
        <BulkBar s={s} entity="orders" prefix="orders" onDelete={bulkDelete}>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className={bulkInp} data-testid="orders-bulk-status">
            <option value="">Cambia stato…</option>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={applyStatus} className="rounded-full bg-black px-3 py-1 text-white" data-testid="orders-bulk-apply">Applica</button>
        </BulkBar>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-black/10">
        <table className="w-full min-w-[820px] text-sm" data-testid="admin-orders-table">
          <thead className="bg-[#FFFFFF] text-left text-xs uppercase tracking-wider text-stone-500">
            <tr><th className="w-10 p-3"><CheckAll s={s} testid="orders-select-all" /></th><th className="p-3">Ordine</th><th className="p-3">Cliente</th><th className="p-3">Data</th><th className="p-3">Pagamento</th><th className="p-3">Totale</th><th className="p-3">Stato</th><th className="p-3">Aggiorna</th></tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-stone-500" data-testid="admin-orders-empty">Nessun ordine</td></tr>}
            {orders.map((o) => (
              <Fragment key={o.id}>
                <tr className={`cursor-pointer border-t border-black/[0.06] hover:bg-black/[0.02] ${s.has(o.id) ? "bg-amber-50" : ""}`} onClick={() => setOpen(open === o.id ? null : o.id)} data-testid={`admin-order-row-${o.id}`}>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}><Check s={s} id={o.id} testid={`order-select-${o.id}`} /></td>
                  <td className="p-3 font-mono font-semibold">{o.order_number}{o.b2b && <span className="ml-1 rounded bg-cyan-700/15 px-1.5 text-[10px] font-bold text-cyan-800" data-testid={`admin-order-b2b-${o.id}`}>B2B</span>}</td>
                  <td className="p-3"><span className="block">{o.shipping.full_name}</span><span className="text-xs text-stone-500">{o.email}</span></td>
                  <td className="p-3 text-stone-600">{new Date(o.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="p-3 text-stone-600">{PAYMENT_LABEL[o.payment_method]}<span className={`block text-xs ${o.payment_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>{o.payment_status === "paid" ? "Pagato" : "Non pagato"}</span></td>
                  <td className="p-3 font-mono text-amber-600">{eur(o.total)}</td>
                  <td className="p-3"><StatusBadge status={o.status} testid={`admin-order-status-${o.id}`} /></td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <select value={o.status} disabled={o.status === "annullato"} onChange={(e) => update(o.id, e.target.value)} data-testid={`admin-order-select-${o.id}`}
                      className="h-9 rounded-lg border border-black/10 bg-[#FFFBF3] px-2 text-xs outline-none disabled:opacity-40">
                      {Object.entries(ORDER_STATUS).filter(([k]) => k !== "pronto_ritiro" || o.delivery === "ritiro").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    {o.delivery === "ritiro" && ["pagato", "in_lavorazione"].includes(o.status) && (
                      <button onClick={() => update(o.id, "pronto_ritiro")} className="mt-1 block rounded-full bg-cyan-700 px-3 py-1 text-xs font-semibold text-white" data-testid={`admin-pickup-ready-${o.id}`}>Pronto per il ritiro</button>
                    )}
                  </td>
                </tr>
                {open === o.id && (
                  <tr className="bg-[#FFF8EC]"><td colSpan={8} className="p-4 text-xs text-stone-700" data-testid={`admin-order-detail-${o.id}`}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>{o.items.map((i) => <p key={i.product_id}>{i.quantity} × {i.name} — {eur(i.price * i.quantity)}</p>)}</div>
                      <div>{o.shipping.address}, {o.shipping.zip} {o.shipping.city} ({o.shipping.province}) · Tel. {o.shipping.phone}{o.notes && <p className="mt-1 text-stone-500">Note: {o.notes}</p>}</div>
                    </div>
                    {o.courier && <p className="mt-3 text-xs text-stone-600" data-testid={`order-courier-${o.id}`}>Corriere: fascia <b>{o.courier.tier}</b> · {o.courier.weight_g} g · {o.courier.volume_cm3} cm³ · costo vivo {eur(o.courier.cost)} · incassato {eur(o.shipping_fee)} · margine <b className={o.courier.margin < 0 ? "text-rose-600" : "text-emerald-700"}>{eur(o.courier.margin)}</b></p>}
                    {o.private_delivery && <p className="mt-3 text-xs text-stone-600" data-testid={`order-private-${o.id}`}>Consegna privata: {o.private_delivery.km} km da Ragusa · tariffa {eur(o.private_delivery.base_fee)} · sconto {o.private_delivery.discount_pct}% · addebitato {eur(o.private_delivery.fee)}</p>}
                    {o.preorder && <p className="mt-2 text-xs font-semibold text-violet-500">Preordine · uscita {o.release_date}{o.deposit ? ` · caparra ${eur(o.deposit)} · saldo ${eur(o.balance_due || 0)}` : ""}</p>}
                    {o.delivery !== "ritiro" && <TrackingForm o={o} onSaved={load} />}
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
